import { useCallback, useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { JobPayload } from "./../create.types";
import { useForm } from "react-hook-form";
import { getMatchJobSkills } from "@endpoints/job.service";
import {
  JobStatus,
  JobType,
  WorkPlaceOptions,
} from "@utils/constants";
import { useSnackbar } from "notistack";
import { isNullOrEmpty } from "@utils/helperFunction";
import {
  addJobRequirement,
  getJobRequirementById,
  getPartnerAgencies,
  grantAccess,
  updateJobRequirement,
} from "@endpoints/hiringCompany/requirements";
import { ICreateJobRequirementPayload, ICreateJobRequirementResponse } from "@type/jobRequirement.type";
import { STEP_CONFIG } from "../AddOrEditJobRequirement";

const useCreateOrEditJobRequirements = () => {
  const { enqueueSnackbar } = useSnackbar();
  const navigate = useNavigate();
  const { id: jobRequirementId } = useParams()
  const location = useLocation();
  const { companies } = useSelector((state: any) => state?.company);

  const state = location?.state as JobPayload | null;

  // Local state
  const [jobRequirementDetails, setJobRequirementDetails] = useState<ICreateJobRequirementResponse>()
  const [step, setStep] = useState<number>(0);

  const defaultValues = useMemo(() => {
    return {
      companiesLoading: companies.length === 0,
      industryType: jobRequirementDetails?.industryType,
      jobDescription: jobRequirementDetails?.description,
      workPlaceOption: jobRequirementDetails?.workPlaceOption
        ? //@ts-ignore
        WorkPlaceOptions.find((x) => jobRequirementDetails?.workPlaceOption === x.value)
          ?.label
        : undefined,
      jobLocation: jobRequirementDetails?.location,
      jobTitle: jobRequirementDetails?.title,
      jobType: jobRequirementDetails?.jobType
        ? //@ts-ignore
        JobType.find((x) => jobRequirementDetails.jobType === x.value)?.label
        : undefined,
      salaryRangeFrom: jobRequirementDetails?.salary?.min,
      salaryRangeTo: jobRequirementDetails?.salary?.max,
      isSalaryHidden: jobRequirementDetails?.isSalaryHidden ?? false,
      selectedCompany: jobRequirementDetails?.companyId
        ? companies.find((x) => x._id === jobRequirementDetails.companyId)
        : undefined,
      createdDraftJob: jobRequirementDetails ?? undefined,
      publishDate: undefined,
      locationProfile: false,
      //@ts-ignore
      referralAmount: jobRequirementDetails?.referralAmount,
      additionalNotes: jobRequirementDetails?.additionalNotes,
      hiringTeamMembers: jobRequirementDetails?.hiringTeamMembers?.map((item) => {
        return { userId: item?.userId }
      }),
      isLoading: false,
      partnerAgencues: [],
      selectedAgents: [],
      isSharedToAllAgencies:jobRequirementDetails?.isSharedToAllAgencies
    };
  }, [companies, jobRequirementDetails]);

  console.log(jobRequirementDetails, 'jobRequirementDetails')

  const {
    control,
    watch,
    setValue,
    trigger,
    reset,
    formState: { errors },
  } = useForm<any>({
    defaultValues,
    mode: "onChange",
  });

  // Reset form whenever defaultValues changes
  useEffect(() => {
    reset(defaultValues);
  }, [defaultValues, reset]);

  console.log('jobRequirementDetails', jobRequirementDetails)

  async function fetchJobRequirement() {
    try {
      const { success, data } = await getJobRequirementById({ jobRequirementId })
      if (success) setJobRequirementDetails(data)
    } catch (error) {
      enqueueSnackbar("Error fetching job requirement details", { variant: 'error' })
      console.error('Error: fetching job requirement details', error)
    }
  }

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [step]);

  // Fetch Job requirement details in case of edit
  useEffect(() => {
    if (jobRequirementId) fetchJobRequirement()
  }, [jobRequirementId]);

  const fetchTJobSkills = async (jobId) => {
    try {
      const { data, success } = await getMatchJobSkills(jobId);
      if (success && data) {
      }
    } catch (error) { }
  };

  const StepComponent = STEP_CONFIG[step].component;

  const fields = watch();

  const fetchData = useCallback(async () => {
    try {
      const response = await getPartnerAgencies();
      setValue("partnerAgencues", response?.data);
    } catch (error) { }
  }, []);

  const filteredPartnerAgencuesData = useMemo(() => {
    if (isNullOrEmpty(fields?.search)) {
      return fields?.partnerAgencues;
    }

    return fields?.partnerAgencues?.filter(
      (x) =>
        x.recruiter.firstName
          ?.toLowerCase()
          ?.includes(fields?.search?.trim()?.toLowerCase()) ||
        x.recruiter.refNo
          ?.toLowerCase()
          ?.includes(fields?.search?.trim()?.toLowerCase())
    );
  }, [fields?.partnerAgencues, fields?.search]);

  const createJob = useCallback(
    async (data) => {
      try {
        setValue("isLoading", true);
        const jobTypeValue = JobType.find(
          (x) => x.label?.toLowerCase() === data?.jobType?.toLowerCase()
        )?.value;
        const workPlaceOptionValue = WorkPlaceOptions.find(
          (x) => x.label?.toLowerCase() === data?.workPlaceOption?.toLowerCase()
        )?.value;

        const cleanSalaryMin = data?.salaryRangeFrom
          ? String(data.salaryRangeFrom).replace(/,/g, "")
          : data?.salaryRangeFrom;

        const cleanSalaryMax = data?.salaryRangeTo
          ? String(data.salaryRangeTo).replace(/,/g, "")
          : data?.salaryRangeTo;

        const jobPayload: ICreateJobRequirementPayload = {
          title: data?.jobTitle,
          description: data?.jobDescription,
          location: data?.jobLocation,
          salary: {
            min: cleanSalaryMin,
            max: cleanSalaryMax,
          },
          industryType: data?.industryType,
          workPlaceOption: workPlaceOptionValue,
          jobType: jobTypeValue,
          additionalNotes: data?.additionalNotes,
          hiringTeamMembers: data?.hiringTeamMembers || [],
          // isSharedToAllAgencies: !(Array.isArray(data?.hiringTeamMembers) && data?.hiringTeamMembers?.length),
          // companyId: data?.selectedCompany?._id,
          status: JobStatus.DRAFT,
          // isSalaryHidden: data?.isSalaryHidden,
        };

        const response = data?.createdDraftJob
          ? await updateJobRequirement({
            id: data?.createdDraftJob?._id,
            ...jobPayload,
          })
          : await addJobRequirement(jobPayload);

        if (response?.success) {
          setValue("createdDraftJob", response?.data);
          setStep((prev) => prev + 1);
          setValue("currentJobId", response.data._id);
        }
      } catch (error) {
        enqueueSnackbar(error, { variant: "error" });
      } finally {
        setValue("isLoading", false);
      }
    },
    [setValue, enqueueSnackbar]
  );

  const finishJob = useCallback(async () => {
    try {
      setValue("isLoading", true);
      const { success } = await await updateJobRequirement({
        id: fields?.createdDraftJob?._id,
        status: JobStatus.LIVE,
        isSharedToAllAgencies: fields?.isSharedToAllAgencies
      });

      if (success) {
        await fetchData();
        setValue("inviteModal", true);
      }
    } catch (error) {
      enqueueSnackbar(error, { variant: "error" });
    } finally {
      setValue("isLoading", false);
    }
  }, [fields, navigate, enqueueSnackbar, fetchData]);

  const handleNext = useCallback(async () => {
    const isStepValid = await trigger();
    switch (step) {
      case 0:
        if (!isStepValid) return;
        setStep(1);
        break;

      case 1:
        if (isNullOrEmpty(fields?.jobDescription)) {
          trigger("jobDescription");
          return;
        }

        await createJob(fields);
        setStep(2);
        break;

      case 2:
        if (fields?.createdDraftJob?._id) {
          await fetchTJobSkills(fields.createdDraftJob._id);
        }
        setStep(3);
        break;
      case 3:
        if (isNullOrEmpty(fields?.additionalNotes)) {
          trigger("additionalNotes");
          return;
        }

        await createJob(fields);
        setStep(4);
        break;
      case 4:
        console.log(fields)
        // if (Array.isArray(fields?.hiringTeamMembers) && !fields?.hiringTeamMembers?.length) {
        //   enqueueSnackbar('You must select at least 1 team member', { variant: 'error' })
        //   return;
        // }
        await createJob(fields);
        setStep(5);
        break;

      case 5:
        await finishJob();

        break;

      default:
        break;
    }
  }, [step, fields, trigger, createJob, fetchTJobSkills, finishJob]);

  const sendJob = async () => {
    try {
      setValue("isSubmitting", true);
      const payload = {
        jobRequirementIds: [fields?.createdDraftJob?._id],
        recruiterIds: fields?.selectedAgents,
      };
      await grantAccess(payload);
      setValue("inviteModal", false);
      setValue("openSucessModel", true);
    } catch (error) {
      console.error(error, "error");
      enqueueSnackbar(error, {
        variant: "error",
      });
    } finally {
      setValue("isSubmitting", false);
    }
  };

  return {
    step,
    control,
    errors,
    fields,
    trigger,
    defaultValues,
    filteredPartnerAgencuesData,
    setValue,
    watch,
    setStep,
    navigate,
    sendJob,
    handleNext,
    StepComponent,
  };
};

export default useCreateOrEditJobRequirements;
